import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import './DeviceNameManagement.css';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import ConfirmationModal from '../../../shared/components/modals/ConfirmationModal';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import ActionMenu from '../../../shared/components/common/ActionMenu';
import { SeverityBadge } from '../../../shared/components/common/Badges';
import { getDeviceNameIcon } from '../utils/deviceNameIconUtils';
import { toBoolean } from '../../../utils/booleanUtils';

function DeviceNameManagement({ token }) {
  const [deviceNames, setDeviceNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDeviceName, setEditingDeviceName] = useState(null);
  const [formData, setFormData] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deviceNameToDelete, setDeviceNameToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteBlockedInfo, setDeleteBlockedInfo] = useState(null);
  const [toggleConfirmation, setToggleConfirmation] = useState({
    isOpen: false,
    deviceName: null,
    action: '',
  });
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    code: '',
    category: '',
    description: '',
    criticality: 'critical'
  });
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const [createError, setCreateError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [criticalityFilter, setCriticalityFilter] = useState(null); // 'critical' | 'major' | 'minor' | null
  const [statusFilter, setStatusFilter] = useState(null); // 'active' | 'inactive' | null

  useEffect(() => {
    fetchDeviceNames();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (editingDeviceName || showCreateModal || showDeleteConfirmation || deleteBlockedInfo || toggleConfirmation.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [editingDeviceName, showCreateModal, showDeleteConfirmation, deleteBlockedInfo, toggleConfirmation.isOpen]);

  const fetchDeviceNames = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.DEVICE_NAMES.LIST_ALL, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDeviceNames(response.data.device_names || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching device names:', error);
      toast.error('Failed to fetch device names');
      setLoading(false);
    }
  };

  const handleEdit = (deviceName) => {
    setEditingDeviceName(deviceName);
    setFormData({
      name: deviceName.name,
      code: deviceName.code,
      category: deviceName.category || '',
      description: deviceName.description || '',
      criticality: deviceName.criticality || 'critical'
    });
  };

  const handleCancelEdit = () => {
    setEditingDeviceName(null);
    setFormData({});
  };

  const handleDeleteClick = (deviceName) => {
    setDeviceNameToDelete(deviceName);
    setShowDeleteConfirmation(true);
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setShowDeleteConfirmation(false);
    setDeviceNameToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!deviceNameToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(API_ENDPOINTS.DEVICE_NAMES.DELETE(deviceNameToDelete.id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Device name deleted successfully!');
      setShowDeleteConfirmation(false);
      setDeviceNameToDelete(null);
      fetchDeviceNames();
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;

      if (status === 400 && data?.device_count != null) {
        setShowDeleteConfirmation(false);
        setDeleteBlockedInfo(data);
      } else {
        toast.error(data?.error || 'Failed to delete device name');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseBlockedInfo = () => {
    setDeleteBlockedInfo(null);
  };

  const handleToggleStatusClick = (deviceName) => {
    const isCurrentlyActive = toBoolean(deviceName.is_active);
    setToggleConfirmation({
      isOpen: true,
      deviceName,
      action: isCurrentlyActive ? 'disable' : 'enable',
    });
  };

  const handleToggleStatusCancel = () => {
    if (isTogglingStatus) return;
    setToggleConfirmation({ isOpen: false, deviceName: null, action: '' });
  };

  const handleToggleStatusConfirm = async () => {
    const { deviceName, action } = toggleConfirmation;
    if (!deviceName) return;
    setIsTogglingStatus(true);

    try {
      const endpoint = action === 'enable'
        ? API_ENDPOINTS.DEVICE_NAMES.ENABLE(deviceName.id)
        : API_ENDPOINTS.DEVICE_NAMES.DISABLE(deviceName.id);

      await axios.post(endpoint, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      toast.success(`Device name ${action === 'enable' ? 'enabled' : 'disabled'} successfully!`);
      setDeviceNames((prev) =>
        prev.map((dn) =>
          dn.id === deviceName.id
            ? { ...dn, is_active: action === 'enable' }
            : dn
        )
      );
      setToggleConfirmation({ isOpen: false, deviceName: null, action: '' });
    } catch (error) {
      console.error('Error toggling device name status:', error);
      toast.error(error.response?.data?.error || `Failed to ${action} device name`);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const validateEditForm = () => {
    if (!formData.name) {
      toast.error('Device name is required');
      return false;
    }
    if (!formData.code) {
      toast.error('Device code is required');
      return false;
    }
    if (!/^[a-zA-Z]{3,5}$/.test(formData.code)) {
      toast.error('Device code must be 3-5 letters (e.g., XRM, mri, Echo)');
      return false;
    }
    if (!['critical', 'major', 'minor'].includes(formData.criticality)) {
      toast.error('Criticality is required');
      return false;
    }
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateEditForm()) return;

    try {
      await axios.put(API_ENDPOINTS.DEVICE_NAMES.UPDATE(editingDeviceName.id), formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      toast.success('Device name updated successfully!');
      setEditingDeviceName(null);
      setFormData({});
      fetchDeviceNames();
    } catch (error) {
      console.error('Error updating device name:', error);
      toast.error(error.response?.data?.error || 'Failed to update device name');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;

    // If user is editing the code field directly, mark it as manually edited
    if (name === 'code') {
      setCodeManuallyEdited(true);
      setCreateFormData(prev => ({ ...prev, code: value }));
    }
    // If user is editing the name field, auto-generate code (unless manually edited)
    else if (name === 'name') {
      setCreateFormData(prev => {
        let generatedCode = value
          .replace(/[^a-zA-Z]/g, '')  // Only letters, no numbers
          .substring(0, 3);  // First 3 letters only

        // Make code unique by checking against existing device names
        if (generatedCode && !codeManuallyEdited) {
          const existingCodes = deviceNames.map(dn => dn.code);

          // If generated code already exists, append letters until unique
          let counter = 0;
          let uniqueCode = generatedCode;
          while (existingCodes.includes(uniqueCode) && counter < 26) {
            // Try appending next letters: first extend to 4 letters, then 5
            if (uniqueCode.length < 5) {
              const charCode = 97 + counter; // a=97 in ASCII
              uniqueCode = generatedCode + String.fromCharCode(charCode);
            } else {
              // If already 5 letters, replace last letter
              const charCode = 97 + counter;
              uniqueCode = generatedCode.substring(0, 4) + String.fromCharCode(charCode);
            }
            counter++;
          }
          generatedCode = uniqueCode;
        }

        return {
          ...prev,
          name: value,
          // Auto-generate code only if user hasn't manually edited it yet
          code: !codeManuallyEdited ? generatedCode : prev.code
        };
      });
    }
    // For other fields (category, description)
    else {
      setCreateFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateCreateForm = () => {
    if (!createFormData.name || !createFormData.code) {
      setCreateError('Please fill in all required fields');
      return false;
    }
    if (!/^[a-zA-Z]{3,5}$/.test(createFormData.code)) {
      setCreateError('Code must be 3-5 letters (e.g., XRM, mri, Echo)');
      return false;
    }
    if (!['critical', 'major', 'minor'].includes(createFormData.criticality)) {
      setCreateError('Criticality is required');
      return false;
    }
    setCreateError('');
    return true;
  };

  const handleCreateDeviceName = async (e) => {
    e.preventDefault();
    if (!validateCreateForm()) return;

    setCreateError('');
    setIsCreating(true);

    try {
      await axios.post(API_ENDPOINTS.DEVICE_NAMES.CREATE, createFormData, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      toast.success(`Device name "${createFormData.name}" created successfully!`);
      setShowCreateModal(false);
      setCreateFormData({
        name: '',
        code: '',
        category: '',
        description: '',
        criticality: 'critical'
      });
      setCodeManuallyEdited(false);
      fetchDeviceNames();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'An unexpected error occurred while creating the device name';
      setCreateError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenCreate = () => {
    setShowCreateModal(true);
  };

  const handleCancelCreate = () => {
    setShowCreateModal(false);
    setCreateError('');
    setCreateFormData({
      name: '',
      code: '',
      category: '',
      description: '',
      criticality: 'critical'
    });
    setCodeManuallyEdited(false);
  };

  const getSearchFilteredDeviceNames = () => {
    if (!searchTerm) return deviceNames;
    const searchLower = searchTerm.toLowerCase();
    return deviceNames.filter(dn =>
      dn.name.toLowerCase().includes(searchLower) ||
      dn.code.toLowerCase().includes(searchLower) ||
      (dn.category && dn.category.toLowerCase().includes(searchLower)) ||
      (dn.description && dn.description.toLowerCase().includes(searchLower)) ||
      (dn.criticality && dn.criticality.toLowerCase().includes(searchLower))
    );
  };

  const handleCriticalityFilterClick = (level) => {
    setStatusFilter(null);
    setCriticalityFilter((prev) => (prev === level ? null : level));
  };

  const handleStatusFilterClick = (status) => {
    setCriticalityFilter(null);
    setStatusFilter((prev) => (prev === status ? null : status));
  };

  const handleTotalFilterClick = () => {
    setStatusFilter(null);
    setCriticalityFilter(null);
  };

  const searchFilteredDeviceNames = getSearchFilteredDeviceNames();
  const statusCounts = searchFilteredDeviceNames.reduce(
    (acc, dn) => {
      if (toBoolean(dn.is_active)) {
        acc.active += 1;
      } else {
        acc.inactive += 1;
      }
      return acc;
    },
    { active: 0, inactive: 0 }
  );
  const criticalityCounts = searchFilteredDeviceNames.reduce(
    (acc, dn) => {
      const key = String(dn.criticality || '').toLowerCase();
      if (key === 'critical' || key === 'major' || key === 'minor') {
        acc[key] += 1;
      }
      return acc;
    },
    { critical: 0, major: 0, minor: 0 }
  );

  let filteredDeviceNames = searchFilteredDeviceNames;
  if (statusFilter) {
    filteredDeviceNames = filteredDeviceNames.filter((dn) => {
      const isActive = toBoolean(dn.is_active);
      return statusFilter === 'active' ? isActive : !isActive;
    });
  } else if (criticalityFilter) {
    filteredDeviceNames = filteredDeviceNames.filter(
      (dn) => String(dn.criticality || '').toLowerCase() === criticalityFilter
    );
  }

  if (loading) {
    return <div className="loading">Loading device names...</div>;
  }

  return (
    <div className="device-name-management">
      <div className="device-names-description-section">
        <h1>📇 Device Name Management</h1>
        <p>Manage standardized device names for biomedical, engineering, and other equipment types</p>
      </div>

      <div className="device-name-toolbar">
        <div className="device-name-stats-block">
          <div className="device-name-stats" aria-live="polite">
          <button
            type="button"
            className={`device-name-summary${!statusFilter && !criticalityFilter ? ' is-active' : ''}`}
            onClick={handleTotalFilterClick}
            title="Clear filters"
            aria-pressed={!statusFilter && !criticalityFilter}
          >
            <span className="summary-count">{searchFilteredDeviceNames.length}</span>
            <span className="summary-label">{searchTerm ? 'Results' : 'Total'}</span>
          </button>
          <button
            type="button"
            className={`device-name-summary device-name-summary-active${statusFilter === 'active' ? ' is-active' : ''}`}
            onClick={() => handleStatusFilterClick('active')}
            title="Show active names only"
            aria-pressed={statusFilter === 'active'}
          >
            <span className="summary-count">{statusCounts.active}</span>
            <span className="summary-label">Active</span>
          </button>
          <button
            type="button"
            className={`device-name-summary device-name-summary-inactive${statusFilter === 'inactive' ? ' is-active' : ''}`}
            onClick={() => handleStatusFilterClick('inactive')}
            title="Show inactive names only"
            aria-pressed={statusFilter === 'inactive'}
          >
            <span className="summary-count">{statusCounts.inactive}</span>
            <span className="summary-label">Inactive</span>
          </button>
          <button
            type="button"
            className={`device-name-summary device-name-summary-critical${criticalityFilter === 'critical' ? ' is-active' : ''}`}
            onClick={() => handleCriticalityFilterClick('critical')}
            title="Filter by Critical"
            aria-pressed={criticalityFilter === 'critical'}
          >
            <span className="summary-count">{criticalityCounts.critical}</span>
            <span className="summary-label">Critical</span>
          </button>
          <button
            type="button"
            className={`device-name-summary device-name-summary-major${criticalityFilter === 'major' ? ' is-active' : ''}`}
            onClick={() => handleCriticalityFilterClick('major')}
            title="Filter by Major"
            aria-pressed={criticalityFilter === 'major'}
          >
            <span className="summary-count">{criticalityCounts.major}</span>
            <span className="summary-label">Major</span>
          </button>
          <button
            type="button"
            className={`device-name-summary device-name-summary-minor${criticalityFilter === 'minor' ? ' is-active' : ''}`}
            onClick={() => handleCriticalityFilterClick('minor')}
            title="Filter by Minor"
            aria-pressed={criticalityFilter === 'minor'}
          >
            <span className="summary-count">{criticalityCounts.minor}</span>
            <span className="summary-label">Minor</span>
          </button>
          </div>
          <p className="device-name-stats-help">
            Pick one filter at a time (status or criticality). Inactive names stay on existing devices but cannot be assigned to new ones.
          </p>
        </div>
        <div className="action-palette">
          <button type="button" className="action-btn action-primary" onClick={handleOpenCreate}>
            <span className="action-icon">➕</span>
            <span className="action-text">Add New Device Name</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-box-wrapper">
          <span className="search-icon-lens">🔍</span>
          <input
            type="text"
            placeholder="Search by device name, code, category, description, or criticality..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input-clean"
          />
          {searchTerm && (
            <button
              className="clear-search-btn-inline"
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Device Names Grid */}
      <div className="device-names-grid">
        {filteredDeviceNames.length > 0 ? (
          filteredDeviceNames.map((deviceName) => {
            const isActive = toBoolean(deviceName.is_active);
            return (
            <div
              key={deviceName.id}
              className={`device-name-card${isActive ? '' : ' inactive'}`}
            >
              <div className="device-name-card-header">
                <div className="device-name-icon-section">
                  <span className="device-name-icon">{getDeviceNameIcon(deviceName)}</span>
                </div>
                <div className="device-name-section">
                  <h3>{deviceName.name}</h3>
                  <div className="device-name-badges">
                    <span className="device-name-code-badge">{deviceName.code}</span>
                    <span className={`device-name-status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <ActionMenu
                  ariaLabel={`Actions for ${deviceName.name}`}
                  actions={[
                    {
                      id: 'edit',
                      icon: '✏️',
                      label: 'Edit',
                      onClick: () => handleEdit(deviceName),
                    },
                    {
                      id: 'toggle',
                      icon: isActive ? '🚫' : '✓',
                      label: isActive ? 'Disable' : 'Enable',
                      danger: isActive,
                      onClick: () => handleToggleStatusClick(deviceName),
                    },
                    {
                      id: 'delete',
                      icon: '🗑️',
                      label: 'Delete',
                      danger: true,
                      onClick: () => handleDeleteClick(deviceName),
                    },
                  ]}
                />
              </div>

              {(deviceName.category || deviceName.criticality) && (
                <div className="device-name-meta">
                  {deviceName.category && (
                    <span className="device-name-category-chip">{deviceName.category}</span>
                  )}
                  {deviceName.criticality && (
                    <SeverityBadge severity={deviceName.criticality} size="medium" />
                  )}
                </div>
              )}

              {deviceName.description && (
                <div className="device-name-description">
                  <p>{deviceName.description}</p>
                </div>
              )}
            </div>
            );
          })
        ) : (
          <div className="empty-state">
            <p>
              {searchTerm || criticalityFilter || statusFilter
                ? 'No device names found matching your filters.'
                : 'No device names found. Create your first device name to get started!'}
            </p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingDeviceName && ReactDOM.createPortal(
        <div className="dn-modal-overlay">
          <div className="dn-modal">
            <div className="dn-modal-header">
              <h3>Edit Device Name</h3>
              <ModalCloseButton onClick={handleCancelEdit} title="Close" />
            </div>
            <form onSubmit={handleSave}>
              <div className="dn-modal-body">
                <div className="dn-form-grid">
                  <div className="dn-form-row-2">
                    <div className="dn-form-group">
                      <label htmlFor="dn-edit-name">Name *</label>
                      <input
                        id="dn-edit-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                        autoFocus
                      />
                    </div>
                    <div className="dn-form-group">
                      <label htmlFor="dn-edit-code">Code *</label>
                      <input
                        id="dn-edit-code"
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleFormChange}
                        required
                        maxLength="5"
                        placeholder="e.g. EEG"
                        title="Device code must be 3-5 letters (case-insensitive)"
                      />
                      <small>3–5 letters</small>
                    </div>
                  </div>

                  <div className="dn-form-row-2">
                    <div className="dn-form-group">
                      <label htmlFor="dn-edit-category">Category</label>
                      <input
                        id="dn-edit-category"
                        type="text"
                        name="category"
                        value={formData.category}
                        onChange={handleFormChange}
                        placeholder="e.g. Imaging"
                      />
                    </div>
                    <div className="dn-form-group">
                      <label htmlFor="dn-edit-criticality">Criticality *</label>
                      <select
                        id="dn-edit-criticality"
                        name="criticality"
                        value={formData.criticality || 'critical'}
                        onChange={handleFormChange}
                        required
                      >
                        <option value="critical">Critical</option>
                        <option value="major">Major</option>
                        <option value="minor">Minor</option>
                      </select>
                    </div>
                  </div>

                  <div className="dn-form-group">
                    <label htmlFor="dn-edit-description">Description</label>
                    <textarea
                      id="dn-edit-description"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      rows="2"
                      placeholder="Brief description of the device"
                    />
                  </div>
                </div>
              </div>
              <div className="dn-modal-footer">
                <button type="button" className="action-btn action-secondary" onClick={handleCancelEdit}>
                  Cancel
                </button>
                <button type="submit" className="action-btn action-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Create Modal */}
      {showCreateModal && ReactDOM.createPortal(
        <div className="dn-modal-overlay">
          <div className="dn-modal">
            <div className="dn-modal-header">
              <h3>Add Device Name</h3>
              <ModalCloseButton onClick={handleCancelCreate} title="Close" />
            </div>
            <form onSubmit={handleCreateDeviceName}>
              <div className="dn-modal-body">
                {createError && <div className="dn-modal-error">{createError}</div>}
                <div className="dn-form-grid">
                  <div className="dn-form-row-2">
                    <div className="dn-form-group">
                      <label htmlFor="dn-create-name">Name *</label>
                      <input
                        id="dn-create-name"
                        type="text"
                        name="name"
                        value={createFormData.name}
                        onChange={handleCreateFormChange}
                        required
                        placeholder="e.g. X-Ray Machine"
                        autoFocus
                      />
                    </div>
                    <div className="dn-form-group">
                      <label htmlFor="dn-create-code">Code *</label>
                      <input
                        id="dn-create-code"
                        type="text"
                        name="code"
                        value={createFormData.code}
                        onChange={handleCreateFormChange}
                        required
                        maxLength="5"
                        placeholder="Auto or custom"
                        pattern="[a-zA-Z]{3,5}"
                        title="Device code must be 3-5 letters (case-insensitive)"
                      />
                      <small>
                        {!codeManuallyEdited && createFormData.code
                          ? 'Auto-generated from name — edit to customize'
                          : '3–5 letters'}
                      </small>
                    </div>
                  </div>

                  <div className="dn-form-row-2">
                    <div className="dn-form-group">
                      <label htmlFor="dn-create-category">Category</label>
                      <input
                        id="dn-create-category"
                        type="text"
                        name="category"
                        value={createFormData.category}
                        onChange={handleCreateFormChange}
                        placeholder="e.g. Imaging"
                      />
                    </div>
                    <div className="dn-form-group">
                      <label htmlFor="dn-create-criticality">Criticality *</label>
                      <select
                        id="dn-create-criticality"
                        name="criticality"
                        value={createFormData.criticality}
                        onChange={handleCreateFormChange}
                        required
                      >
                        <option value="critical">Critical</option>
                        <option value="major">Major</option>
                        <option value="minor">Minor</option>
                      </select>
                    </div>
                  </div>

                  <div className="dn-form-group">
                    <label htmlFor="dn-create-description">Description</label>
                    <textarea
                      id="dn-create-description"
                      name="description"
                      value={createFormData.description}
                      onChange={handleCreateFormChange}
                      placeholder="Brief description of the device"
                      rows="2"
                    />
                  </div>
                </div>
              </div>
              <div className="dn-modal-footer">
                <button type="button" className="action-btn action-secondary" onClick={handleCancelCreate} disabled={isCreating}>
                  Cancel
                </button>
                <button type="submit" className="action-btn action-primary" disabled={isCreating}>
                  {isCreating ? 'Creating...' : 'Create Device Name'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        entityName={deviceNameToDelete ? `${deviceNameToDelete.name} (${deviceNameToDelete.code})` : 'this device name'}
        entityType="device name"
        action="delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
        customMessage="You can delete this device name only if it is not currently assigned to any devices."
      />

      <ConfirmationModal
        isOpen={toggleConfirmation.isOpen}
        entityName={
          toggleConfirmation.deviceName
            ? `${toggleConfirmation.deviceName.name} (${toggleConfirmation.deviceName.code})`
            : 'this device name'
        }
        entityType="device name"
        action={toggleConfirmation.action || 'disable'}
        onConfirm={handleToggleStatusConfirm}
        onCancel={handleToggleStatusCancel}
        isLoading={isTogglingStatus}
        customMessage={
          toggleConfirmation.action === 'disable'
            ? 'Existing devices keep this name. New devices will not be able to use it until re-enabled.'
            : 'This device name will be available for new devices again.'
        }
      />

      {/* Delete Blocked Modal (In Use) */}
      {deleteBlockedInfo && ReactDOM.createPortal(
        <div className="dn-modal-overlay">
          <div className="dn-modal dn-modal-wide">
            <div className="dn-modal-header">
              <h3>Cannot Delete Device Name</h3>
              <ModalCloseButton onClick={handleCloseBlockedInfo} title="Close" />
            </div>
            <div className="dn-modal-body">
              <div className="dn-modal-error">
                {deleteBlockedInfo.device_name
                  ? `“${deleteBlockedInfo.device_name}” is used in ${deleteBlockedInfo.device_count} devices.`
                  : `This device name is used in ${deleteBlockedInfo.device_count} devices.`}
                {deleteBlockedInfo.devices_truncated ? ` Showing ${deleteBlockedInfo.devices_returned}.` : ''}
              </div>

              {Array.isArray(deleteBlockedInfo.devices) && deleteBlockedInfo.devices.length > 0 ? (
                <div style={{ maxHeight: '420px', overflow: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#fafafa' }}>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Identifier</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Location</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Department</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Model</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Serial</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deleteBlockedInfo.devices.map((d) => (
                        <tr key={d.id}>
                          <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1', fontWeight: 600 }}>
                            {d.device_identifier || '-'}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                            {d.location_name || '-'}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                            {d.department || '-'}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                            {d.model || '-'}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                            {d.serial_number || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: '#666' }}>No device details were returned.</p>
              )}
            </div>
            <div className="dn-modal-footer">
              <button type="button" className="action-btn action-primary" onClick={handleCloseBlockedInfo}>
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default DeviceNameManagement;
